import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CheckIcon, ChevronLeftIcon } from '@heroicons/react/20/solid';
import { bookingApi } from '../../utils/bookingApi';
import { summaryFrom } from './CheckoutSummary';
import { useFeature } from '../../hooks/useFeature';
import { useAuth } from '../../contexts/AuthContext';
import { PageHeader, Sheet, useConfirm, Skeleton, ErrorState, EmptyState, InlineError } from '../../components/ui';
import { formatDayDate, formatTime } from '../../utils/dates';
import './booking-shared.css';
import './SessionDetail.css';
import CompetesAsField from '../../components/CompetesAsField';
import { typeOf } from '../../utils/sessionTypes';

// One session: who's coming, the price and Book. As its own page it has the
// usual header; inside the calendar (onClose) it sits in the day's panel and
// goes back to that day's sessions.
export default function SessionDetail({
  instanceId: propInstanceId,
  onClose,
  cartMode = false,
  cartGymnastIds = [],
  onCartUpdate = () => {},
}) {
  const { instanceId: paramInstanceId } = useParams();
  const instanceId = propInstanceId || paramInstanceId;
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { can } = useAuth();
  // Tracking a session's gymnasts opens Gymnasts filtered by its register
  const canTrackSession = can('skillTracking') && can('registers');
  const bgBlocks = useFeature('bgBlocking');
  const [session, setSession] = useState(null);
  const [myGymnasts, setMyGymnasts] = useState([]);
  const [gymnastsError, setGymnastsError] = useState(false);
  const [localSelectedGymnastIds, setLocalSelectedGymnastIds] = useState([]);
  const selectedGymnastIds = cartMode ? cartGymnastIds : localSelectedGymnastIds;
  // The server's figures for booking the chosen gymnasts (price, credit, the
  // 30p card minimum): null while loading or with nobody chosen, false if
  // they couldn't be had
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [booking, setBooking] = useState(false);
  const [error, setError] = useState(null);
  const [waitlistEntry, setWaitlistEntry] = useState(null);
  const [waitlistBusy, setWaitlistBusy] = useState(false);
  const [myCommitments, setMyCommitments] = useState([]); // [{ gymnastId, status }]
  const [showSelfDobForm, setShowSelfDobForm] = useState(false);
  const [selfDob, setSelfDob] = useState('');
  const [selfGender, setSelfGender] = useState('');
  const [selfError, setSelfError] = useState(null);
  const [selfSaving, setSelfSaving] = useState(false);
  const [conflictingGymnastIds, setConflictingGymnastIds] = useState(new Set());

  const loadGymnasts = () => {
    setGymnastsError(false);
    return bookingApi.getBookableGymnasts()
      .then(r => setMyGymnasts(r.data))
      .catch(() => { setMyGymnasts([]); setGymnastsError(true); });
  };

  useEffect(() => {
    setLoading(true);
    setLoadError(false);
    Promise.all([
      bookingApi.getSession(instanceId),
      bookingApi.getMyWaitlist().catch(() => ({ data: [] })),
      bookingApi.getMyBookings().catch(() => ({ data: [] })),
    ]).then(([sessRes, waitRes, bookingsRes]) => {
      const sess = sessRes.data;
      setSession(sess);
      const entry = waitRes.data.find(e => e.sessionInstanceId === instanceId);
      setWaitlistEntry(entry || null);
      if (sess.templateId) {
        bookingApi.getMyCommitmentsForTemplate(sess.templateId)
          .then(r => setMyCommitments(r.data))
          // Without them, the server still turns away a second booking
          .catch(() => {});
      }
      // Compute gymnasts with time-overlapping bookings on the same day
      const sessDate = new Date(sess.date).toDateString();
      const conflictIds = new Set();
      for (const booking of bookingsRes.data) {
        if (booking.sessionInstance.id === instanceId) continue;
        if (new Date(booking.sessionInstance.date).toDateString() !== sessDate) continue;
        const { startTime, endTime } = booking.sessionInstance.template;
        if (sess.startTime < endTime && startTime < sess.endTime) {
          booking.lines.forEach(l => conflictIds.add(l.gymnast.id));
        }
      }
      setConflictingGymnastIds(conflictIds);
    }).catch(err => { console.error(err); setLoadError(true); }).finally(() => setLoading(false));

    loadGymnasts();
  }, [instanceId, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const closeSelfForm = () => {
    setShowSelfDobForm(false);
    setSelfDob('');
    setSelfGender('');
    setSelfError(null);
  };

  const handleSelfDobSubmit = async () => {
    if (!selfDob || !selfGender) {
      setSelfError(!selfDob ? 'Enter your date of birth' : 'Choose what you compete as');
      return;
    }
    setSelfSaving(true);
    setSelfError(null);
    try {
      const res = await bookingApi.createSelfGymnast({ dateOfBirth: selfDob, gender: selfGender });
      closeSelfForm();
      await loadGymnasts();
      setLocalSelectedGymnastIds(ids => ids.includes(res.data.id) ? ids : [...ids, res.data.id]);
    } catch (err) {
      setSelfError("Couldn't add you. Please try again.");
    } finally {
      setSelfSaving(false);
    }
  };

  // Filter gymnasts by age restriction
  const eligibleGymnasts = session?.minAge
    ? myGymnasts.filter(g => {
        if (!g.dateOfBirth) return false;
        const age = Math.floor((new Date(session.date) - new Date(g.dateOfBirth)) / (365.25 * 24 * 60 * 60 * 1000));
        return age >= session.minAge;
      })
    : myGymnasts;

  const alreadyBookedGymnastIds = new Set(
    session?.bookings?.filter(b => b.status === 'CONFIRMED').flatMap(b => b.lines.map(l => l.gymnast.id)) ?? []
  );

  const bookableGymnasts = eligibleGymnasts.filter(g => !g.hasMembership);
  const memberGymnasts = eligibleGymnasts.filter(g => g.hasMembership);

  const hasSelf = myGymnasts.some(g => g.isSelf);

  // Gymnasts with a standing slot that covers this session are booked already
  const standingGymnasts = myGymnasts.filter(g => {
    const c = myCommitments.find(mc => mc.gymnastId === g.id);
    return c?.status === 'ACTIVE' && (!c.startDate || new Date(c.startDate) <= new Date(session?.date));
  });
  const standingNames = standingGymnasts.map(g => g.firstName).join(' and ');
  // A full session: the waitlist is only for gymnasts not in it already
  const canWaitFor = bookableGymnasts.some(g => !standingGymnasts.includes(g) && !alreadyBookedGymnastIds.has(g.id));

  const toggleGymnast = (id) => {
    if (cartMode) {
      const isSelected = cartGymnastIds.includes(id);
      const gymnast = myGymnasts.find(g => g.id === id);
      const currentSelected = myGymnasts.filter(g => cartGymnastIds.includes(g.id));
      const newGymnasts = isSelected
        ? currentSelected.filter(g => g.id !== id)
        : [...currentSelected, gymnast];
      onCartUpdate(instanceId, newGymnasts);
    } else {
      setLocalSelectedGymnastIds(prev =>
        prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]
      );
    }
  };

  // What booking the chosen gymnasts would charge, from the server (no sums here)
  const selectedKey = selectedGymnastIds.join(',');
  useEffect(() => {
    if (cartMode || !selectedKey) { setQuote(null); return undefined; }
    let live = true;
    setQuote(null);
    bookingApi.quoteCheckout({ sessions: [{ sessionInstanceId: instanceId, gymnastIds: selectedKey.split(',') }] })
      .then(r => { if (live) setQuote(r.data); })
      .catch(() => { if (live) setQuote(false); });
    return () => { live = false; };
  }, [instanceId, selectedKey, cartMode]);
  const quoted = quote && !quote.refusal ? quote : null;
  const chargeAmount = quoted ? quoted.amount : 0;

  const handleBook = async () => {
    if (selectedGymnastIds.length === 0) return;
    setBooking(true);
    setError(null);
    try {
      const res = await bookingApi.createBooking({
        sessionInstanceId: instanceId,
        gymnastIds: selectedGymnastIds,
      });

      if (res.data.clientSecret) {
        navigate(`/booking/checkout/${res.data.booking.id}`, {
          state: { clientSecret: res.data.clientSecret, stripeAccountId: res.data.stripeAccountId || null, summary: summaryFrom(res.data) },
        });
      } else {
        navigate(`/booking/confirmation/${res.data.booking.id}`);
      }
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't book. Please try again.");
    } finally {
      setBooking(false);
    }
  };

  const handleJoinWaitlist = async () => {
    setWaitlistBusy(true);
    try {
      const res = await bookingApi.joinWaitlist(instanceId);
      setWaitlistEntry(res.data);
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't join the waitlist.");
    } finally {
      setWaitlistBusy(false);
    }
  };

  const handleLeaveWaitlist = async () => {
    setWaitlistBusy(true);
    try {
      await bookingApi.leaveWaitlist(instanceId);
      setWaitlistEntry(null);
    } catch (err) {
      setError("Couldn't leave the waitlist.");
    } finally {
      setWaitlistBusy(false);
    }
  };

  // A member's gymnast doesn't normally book, so a booking is an extra
  const chooseMember = async (g) => {
    const ok = await confirm({
      title: 'Book an extra session?',
      message: `${g.firstName} has a monthly membership and doesn't normally need to book. This uses one of the open slots and costs £${(session.pricePerGymnast / 100).toFixed(2)}.`,
      confirmLabel: 'Book extra session',
      tone: 'primary',
    });
    if (ok) toggleGymnast(g.id);
  };

  const embedded = Boolean(onClose);
  const back = { to: '/booking', label: 'Calendar' };
  const times = session ? `${session.startTime} to ${session.endTime}` : '';

  // The top: a full page header, or in the calendar a way back to the day
  const top = (title, details) => (embedded ? (
    <>
      <button type="button" className="ui-backlink session-detail__back" onClick={onClose}>
        <ChevronLeftIcon aria-hidden="true" />
        <span>Sessions</span>
      </button>
      {title && <h2 className="bk-section-title">{title}</h2>}
    </>
  ) : (
    <PageHeader back={back} title={title || 'Session'} details={details} />
  ));

  const wrapper = embedded ? 'session-detail session-detail--embedded' : 'bk-page bk-page--narrow session-detail';

  if (loading) return <div className={wrapper}>{top(null)}<Skeleton rows={3} label="Loading session…" /></div>;
  if (loadError) {
    return (
      <div className={wrapper}>
        {top(null)}
        <ErrorState title="Couldn't load this session" onRetry={() => setReloadKey(k => k + 1)} />
      </div>
    );
  }
  if (!session) {
    return (
      <div className={wrapper}>
        {top(null)}
        <EmptyState title="Session not found" text="It may have been moved or removed. Pick another from the calendar." />
      </div>
    );
  }

  const slotsClass = !session.cancelledAt && session.availableSlots <= 3 ? 'session-detail__slots--few'
    : !session.cancelledAt && session.availableSlots <= 5 ? 'session-detail__slots--some'
    : '';

  // A tappable row for one gymnast, ticked when chosen
  const option = ({ g, selected, disabled, onPick, children }) => (
    <div
      role="checkbox"
      aria-checked={selected}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : 0}
      className={[
        'session-detail__gymnast-option',
        selected ? 'session-detail__gymnast-option--selected' : '',
        disabled ? 'session-detail__gymnast-option--disabled' : '',
      ].join(' ')}
      onClick={disabled ? undefined : onPick}
      onKeyDown={disabled ? undefined : (e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); onPick(); } }}
    >
      <span className="session-detail__option-check">
        {selected && <CheckIcon aria-hidden="true" />}
      </span>
      {children}
    </div>
  );

  return (
    <div className={wrapper}>
      {top(embedded ? times : formatDayDate(session.date), embedded ? null : times)}

      <div className="session-detail__info">
        {session.minAge && <p><span className="bk-pill bk-pill--info">{session.minAge}+ only</span></p>}
        <p>
          <strong className={slotsClass || undefined}>{session.availableSlots}</strong>{' '}of {session.capacity} slots available
        </p>
        {session.cancelledAt && (
          <p className="session-detail__cancelled">This session has been cancelled.</p>
        )}
        {canTrackSession && (
          <Link to={`/gymnasts?session=${instanceId}`} className="bk-btn session-detail__track">
            Track these gymnasts
          </Link>
        )}
      </div>

      {session.information && (
        <div
          className="bk-session-info session-detail__information"
          dangerouslySetInnerHTML={{ __html: session.information }}
        />
      )}

      {!session.cancelledAt && session.availableSlots === 0 && standingGymnasts.length > 0 && (
        <p className="session-detail__note session-detail__note--good">
          {standingGymnasts.length === 1
            ? `${standingNames} has a standing slot, so is booked for this session.`
            : `${standingNames} have standing slots, so are booked for this session.`}
        </p>
      )}

      {!session.cancelledAt && session.availableSlots === 0
        && (canWaitFor || standingGymnasts.length === 0 || waitlistEntry) && (
        <div className="session-detail__waitlist">
          {waitlistEntry?.status === 'OFFERED' ? (
            <p className="session-detail__waitlist-offered">
              {waitlistEntry.offerType === 'OPEN'
                ? "A slot has come up close to session time. We've let everyone on the waitlist know: first to book gets it."
                : `A slot has been held for you until ${formatTime(waitlistEntry.offerExpiresAt)}.`
              }
            </p>
          ) : waitlistEntry?.status === 'WAITING' ? (
            <>
              <p className="session-detail__waitlist-waiting">
                You're on the waitlist. We'll let you know if a slot becomes available.
              </p>
              <button
                type="button"
                className="bk-btn bk-btn--outline-danger bk-btn--full"
                onClick={handleLeaveWaitlist}
                disabled={waitlistBusy}
              >
                {waitlistBusy ? 'Leaving…' : 'Leave waitlist'}
              </button>
            </>
          ) : (
            <>
              <p className="session-detail__waitlist-waiting">This session is full.</p>
              <button
                type="button"
                className="bk-btn bk-btn--primary bk-btn--full"
                onClick={handleJoinWaitlist}
                disabled={waitlistBusy}
              >
                {waitlistBusy ? 'Joining…' : 'Join waitlist'}
              </button>
            </>
          )}
          {error && <p className="bk-form-error" role="alert">{error}</p>}
        </div>
      )}

      {!session.cancelledAt && (session.availableSlots > 0 || waitlistEntry?.status === 'OFFERED') && (
        <>
          <div className="session-detail__gymnasts">
            <h3>Who's coming?</h3>

            {gymnastsError && <InlineError onRetry={loadGymnasts}>Couldn't load your gymnasts.</InlineError>}

            {bookableGymnasts.map(g => {
              // A type that needs approval: only gymnasts a coach has approved
              const sessionType = typeOf(session);
              const dmtBlocked = !!sessionType?.needsApproval && !(sessionType.id
                ? (g.approvedSessionTypeIds || []).includes(sessionType.id)
                : g.dmtApproved);
              const myCommitment = myCommitments.find(c => c.gymnastId === g.id);
              // A standing slot that hasn't started yet doesn't stop booking this one
              const hasActiveCommitment = myCommitment?.status === 'ACTIVE'
                && (!myCommitment.startDate || new Date(myCommitment.startDate) <= new Date(session.date));
              const alreadyBooked = alreadyBookedGymnastIds.has(g.id);
              const hasTimeConflict = conflictingGymnastIds.has(g.id);
              const selected = selectedGymnastIds.includes(g.id);
              const now = Date.now();
              const bgBlocked = bgBlocks && (() => {
                if (!g.bgNumber && (g.pastSessionCount >= 2 || g.hasMembership)) return true;
                if (g.bgNumberStatus === 'INVALID') return true;
                if (g.bgNumberStatus === 'PENDING' && g.bgNumberEnteredAt && g.bgNumberGraceDays) {
                  const graceMs = g.bgNumberGraceDays * 24 * 60 * 60 * 1000;
                  if (now - new Date(g.bgNumberEnteredAt) > graceMs) return true;
                }
                return false;
              })();
              const atCapacity = !selected && selectedGymnastIds.length >= session.availableSlots;
              const blocked = bgBlocked || dmtBlocked || hasActiveCommitment || alreadyBooked || hasTimeConflict;
              return (
                <div key={g.id}>
                  {option({
                    g,
                    selected,
                    disabled: atCapacity || blocked,
                    onPick: () => toggleGymnast(g.id),
                    children: (
                      <span>{g.firstName} {g.lastName}{g.isSelf ? ' (me)' : ''}
                        {dmtBlocked && (
                          <span className="session-detail__inline-note">
                            Not approved for {sessionType.name}, speak to a coach.
                          </span>
                        )}
                      </span>
                    ),
                  })}
                  {bgBlocked && (
                    <p className="session-detail__note session-detail__note--bad">
                      {g.bgNumberStatus === 'INVALID'
                        ? <>BG number couldn't be verified. <Link to="/booking/my-account">Check it in My Account</Link></>
                        : g.bgNumberStatus === 'PENDING'
                          ? <>BG number is still being verified. Bookings are blocked until it's confirmed. <Link to="/booking/my-account">Check it in My Account</Link></>
                          : <>BG membership number needed. <Link to="/booking/my-account">Add it in My Account</Link></>}
                    </p>
                  )}
                  {bgBlocks && !bgBlocked && !g.bgNumber && g.pastSessionCount === 1 && (
                    <p className="session-detail__note session-detail__note--wait">
                      1 free session left. A BG number will be needed after this.{' '}
                      <Link to="/booking/my-account">Add it in My Account</Link>
                    </p>
                  )}
                  {hasActiveCommitment && (
                    <p className="session-detail__note session-detail__note--good">
                      Standing slot: you're already booked for this session.
                    </p>
                  )}
                  {alreadyBooked && !hasActiveCommitment && (
                    <p className="session-detail__note">Already booked for this session.</p>
                  )}
                  {hasTimeConflict && !alreadyBooked && !hasActiveCommitment && (
                    <p className="session-detail__note">Already booked into another session at this time.</p>
                  )}
                </div>
              );
            })}
            {memberGymnasts.map(g => {
              const selected = selectedGymnastIds.includes(g.id);
              const alreadyBooked = alreadyBookedGymnastIds.has(g.id);
              const hasTimeConflict = conflictingGymnastIds.has(g.id);
              const atCapacity = !selected && selectedGymnastIds.length >= session.availableSlots;
              return (
                <div key={g.id}>
                  {option({
                    g,
                    selected,
                    disabled: (atCapacity && !selected) || alreadyBooked || hasTimeConflict,
                    onPick: () => {
                      if (selected) toggleGymnast(g.id);
                      else chooseMember(g);
                    },
                    children: (
                      <>
                        <span>{g.firstName} {g.lastName}</span>
                        <span className="bk-pill bk-pill--info session-detail__member">Member</span>
                      </>
                    ),
                  })}
                  {alreadyBooked && (
                    <p className="session-detail__note">Already booked for this session.</p>
                  )}
                  {hasTimeConflict && !alreadyBooked && (
                    <p className="session-detail__note">Already booked into another session at this time.</p>
                  )}
                </div>
              );
            })}
            {selectedGymnastIds.length >= session.availableSlots && bookableGymnasts.length > selectedGymnastIds.length && (
              <p className="session-detail__slots-warning">
                Only {session.availableSlots} slot{session.availableSlots !== 1 ? 's' : ''} available. Untick someone to change who's coming.
              </p>
            )}

            {session.minAge && myGymnasts.length > eligibleGymnasts.length && (
              <p className="session-detail__hint">
                Some people are hidden because they don't meet the {session.minAge}+ age requirement.
              </p>
            )}

            {!hasSelf && (
              <button type="button" className="bk-btn session-detail__self" onClick={() => setShowSelfDobForm(true)}>
                Book for myself
              </button>
            )}

            {!gymnastsError && !myGymnasts.some(g => !g.isSelf) && (
              <p className="session-detail__hint">
                No children added yet.{' '}
                <Link to="/booking/my-account" className="bk-link">Add a child in My Account</Link>
                {' '}to book for them.
              </p>
            )}
          </div>

          {cartMode ? (
            selectedGymnastIds.length > 0 && (
              <p className="session-detail__added">
                {selectedGymnastIds.length} gymnast{selectedGymnastIds.length !== 1 ? 's' : ''} added to cart
              </p>
            )
          ) : (
            <>
              {quoted && quoted.creditApplied > 0 && (
                <p className="session-detail__credit-notice">
                  £{(quoted.creditApplied / 100).toFixed(2)} credit will be applied automatically.
                </p>
              )}
              {selectedGymnastIds.length > 0 && (
                <div className="session-detail__summary" data-testid="session-summary">
                  {quote === null && <p>Working out the total…</p>}
                  {quote === false && <p>The total couldn't be loaded just now. The exact amount is shown before you pay.</p>}
                  {quote && quote.refusal && <p className="session-detail__error">{quote.refusal.error}</p>}
                  {quoted && (
                    <>
                      <p>Gymnasts: {quoted.lines[0].quantity} × £{(quoted.lines[0].unitAmount / 100).toFixed(2)} = £{(quoted.total / 100).toFixed(2)}</p>
                      {quoted.creditApplied > 0 && <p>Credits applied: −£{(quoted.creditApplied / 100).toFixed(2)}</p>}
                      <p><strong>Total: £{(quoted.amount / 100).toFixed(2)}</strong></p>
                    </>
                  )}
                </div>
              )}
              {error && <p className="bk-form-error" role="alert">{error}</p>}
              <button
                type="button"
                className="bk-btn bk-btn--primary bk-btn--full"
                disabled={selectedGymnastIds.length === 0 || booking || quote === null || !!(quote && quote.refusal)}
                onClick={handleBook}
              >
                {booking ? 'Booking…' : !quoted ? 'Book' : `Book${chargeAmount > 0 ? `: £${(chargeAmount / 100).toFixed(2)}` : ' (free)'}`}
              </button>
            </>
          )}
        </>
      )}

      {showSelfDobForm && (
        <Sheet
          title="Book for myself"
          onClose={closeSelfForm}
          dirty={Boolean(selfDob || selfGender)}
          onSubmit={handleSelfDobSubmit}
          footer={(requestClose) => (
            <div className="bk-actions">
              <button type="submit" className="bk-btn bk-btn--primary" disabled={selfSaving}>{selfSaving ? 'Saving…' : 'Save'}</button>
              <button type="button" className="bk-btn" onClick={requestClose}>Cancel</button>
            </div>
          )}
        >
          <p className="session-detail__hint">We need these once, to add you as a gymnast.</p>
          <label className="bk-label">
            Date of birth
            <input
              type="date"
              className="bk-input"
              value={selfDob}
              onChange={e => setSelfDob(e.target.value)}
              aria-invalid={selfError && !selfDob ? 'true' : undefined}
            />
          </label>
          <CompetesAsField self value={selfGender} onChange={setSelfGender} />
          {selfError && <p className="bk-form-error" role="alert">{selfError}</p>}
        </Sheet>
      )}
    </div>
  );
}
